import { useEffect, useRef } from 'react';
import { useGoogleAnalyticsEvents, useRedux } from 'hooks';
import { useAuth0 } from "@auth0/auth0-react";
import { loginUser } from 'redux/actions';
import { APICore, setAuthorization } from 'helpers/api/apiCore';
import { useApiCalls } from 'helpers/api/useApiCalls';
import { AuthActionTypes } from '../../redux/auth/constants';
import { authApiResponseError, authApiResponseSuccess } from '../../redux/auth/actions';
import Loading from "../../components/Loading";
import { UserAuth0, saveUser } from 'helpers/api/saveUser';
import { GoogleAnalyticsEventEnum } from 'types/GoogleAnalyticsEventType';

const LoginAuth0 = () => {
  const { loginWithRedirect, isAuthenticated, getAccessTokenSilently, logout, user, isLoading } = useAuth0();
  const { dispatch } = useRedux();
  const { Auth0 } = useApiCalls();
  const { trackEvent } = useGoogleAnalyticsEvents();
  const api = new APICore();
  const hasProcessedRef = useRef(false); // Prevenir múltiples ejecuciones

  useEffect(() => {
    // Esperar a que Auth0 termine de procesar el callback
    if (isLoading) {
      return;
    }

    // Si ya procesamos este login, no hacer nada
    if (hasProcessedRef.current && isAuthenticated) {
      return;
    }

    if (isAuthenticated && user) {
      // Marcar como procesado ANTES de hacer la llamada
      hasProcessedRef.current = true;

      const saveData = (token: string) => {
        Auth0.get()
          .then((response: { data: any }) => {
            trackEvent({
              category: GoogleAnalyticsEventEnum.AUTH0_LOGIN_SUCCESSFUL,
              label: `user: ${JSON.stringify(user)}`
            })
            let data: UserAuth0 = saveUser(user, token, response.data);
            api.setLoggedInUser(data);
            dispatch(loginUser(data.username, data.password));
            dispatch(authApiResponseSuccess(AuthActionTypes.LOGIN_USER, data));

            if (data.datalogins?.logins_count == 1) {
              trackEvent({
                category: GoogleAnalyticsEventEnum.AUTH0_FIRST_LOGIN,
                label: `user: ${JSON.stringify(user)}`
              });
              Auth0.firstlogin();
            }
          })
          .catch((error: any) => {
            trackEvent({
              category: GoogleAnalyticsEventEnum.AUTH0_LOGIN_FAILED ,
              label: `error: ${error}`
            });
            console.log(error)
            dispatch(authApiResponseError(AuthActionTypes.LOGIN_USER, error));
            api.setLoggedInUser(null);
            setAuthorization(null);
            // Resetear el flag para permitir reintento
            hasProcessedRef.current = false;
            logout({ returnTo: window.location.origin });
          });
      };

      getAccessTokenSilently()
        .then((token) => {
          setAuthorization(token);
          saveData(token);
        })
        .catch((error: any) => {
          // Si falla, resetear el flag
          hasProcessedRef.current = false;
          console.error('Error getting access token:', error);
        });
    } else if (!isAuthenticated && !isLoading) {
      // Solo hacer redirect si no está cargando y no está autenticado
      // Preservar la ruta actual para redirigir después del login
      const currentPath = window.location.pathname + window.location.search;
      const url = window.location.href;
      const inviteMatches = url.match(/invitation=([^&]+)/);
      const orgMatches = url.match(/organization=([^&]+)/);
      if (inviteMatches && orgMatches) {
        loginWithRedirect({
          organization: orgMatches[1],
          invitation: inviteMatches[1],
          appState: {
            returnTo: currentPath
          }
        });
      } else {
        loginWithRedirect({
          appState: {
            returnTo: currentPath
          }
        });
      }
    }
  }, [isAuthenticated, isLoading, user])

  // Resetear el flag cuando el usuario cierra sesión
  useEffect(() => {
    if (!isAuthenticated) {
      hasProcessedRef.current = false;
    }
  }, [isAuthenticated]);

  return <Loading />;
};

export default LoginAuth0;